import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaAddressCard,
  FaFacebook,
  FaImage,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPhotoVideo,
} from "react-icons/fa";

import {
  DEFAULT_COVER,
  DEFAULT_LOGO,
  resolveMediaUrl,
} from "../utils/sectionProfileUtils";

export default function SectionProfileForm({
  formData,
  galleries,
  saving,
  demoMode,
  onChange,
  onQuillChange,
  onSubmit,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <FormBlock
        icon={<FaInfoCircle />}
        title="Identité de l’association"
        subtitle="Nom public, discipline et responsable affichés sur la fiche."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <InputField
            label="Nom de la section"
            name="nom_section"
            value={formData.nom_section}
            onChange={onChange}
            required
          />

          <InputField
            label="Discipline / activité"
            name="discipline_section"
            value={formData.discipline_section}
            onChange={onChange}
            placeholder="Ex : Judo, Théâtre, Rugby..."
          />

          <InputField
            label="Responsable"
            name="responsable_section"
            value={formData.responsable_section}
            onChange={onChange}
            placeholder="Nom du responsable"
          />

          <InputField
            label="Téléphone"
            name="tel_section"
            value={formData.tel_section}
            onChange={onChange}
            placeholder="06..."
          />

          <InputField
            label="E-mail"
            name="mail_section"
            type="email"
            value={formData.mail_section}
            onChange={onChange}
            placeholder="contact@association.fr"
          />

          <InputField
            label="Site web"
            name="siteweb_section"
            value={formData.siteweb_section}
            onChange={onChange}
            placeholder="https://..."
          />
        </div>
      </FormBlock>

      <FormBlock
        icon={<FaMapMarkerAlt />}
        title="Coordonnées"
        subtitle="Adresse principale de pratique ou de contact."
      >
        <InputField
          label="Adresse"
          name="adresse_section"
          value={formData.adresse_section}
          onChange={onChange}
          placeholder="Adresse complète"
        />
      </FormBlock>

      <FormBlock
        icon={<FaFacebook />}
        title="Réseaux sociaux"
        subtitle="Liens optionnels affichés sur la fiche publique."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <InputField
            label="Facebook"
            name="facebook_section"
            value={formData.facebook_section}
            onChange={onChange}
            placeholder="https://facebook.com/..."
          />

          <InputField
            label="Instagram"
            name="instagram_section"
            value={formData.instagram_section}
            onChange={onChange}
            placeholder="https://instagram.com/..."
          />
        </div>
      </FormBlock>

      <FormBlock
        icon={<FaAddressCard />}
        title="Présentation publique"
        subtitle="Texte affiché dans la partie “À propos” de la page association."
      >
        <div>
          <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
            À propos de l’association
          </label>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
            <ReactQuill
              value={formData.about_content}
              onChange={onQuillChange}
              theme="snow"
              className="min-h-[240px] dark:text-white"
            />
          </div>
        </div>
      </FormBlock>

      <FormBlock
        icon={<FaImage />}
        title="Images principales"
        subtitle="Logo, vignette et images du header de la page association."
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <ImageUrlField
            label="Logo de la section"
            name="logo_section"
            value={formData.logo_section}
            onChange={onChange}
            fallback={DEFAULT_LOGO}
            objectFit="contain"
          />

          <ImageUrlField
            label="Photo vignette section"
            name="photoURL"
            value={formData.photoURL}
            onChange={onChange}
            fallback={DEFAULT_COVER}
          />

          <ImageUrlField
            label="Image slide 1"
            name="header1_URL"
            value={formData.header1_URL}
            onChange={onChange}
            fallback={DEFAULT_COVER}
          />

          <ImageUrlField
            label="Image slide 2"
            name="header2_URL"
            value={formData.header2_URL}
            onChange={onChange}
            fallback={DEFAULT_COVER}
          />
        </div>
      </FormBlock>

      <FormBlock
        icon={<FaPhotoVideo />}
        title="Carrousel photos"
        subtitle="Sélectionnez une galerie utilisée comme diaporama sur la page de l’association."
      >
        <div className="grid gap-4 sm:grid-cols-[1fr_260px]">
          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Galerie carrousel
            </label>

            <select
              name="carrousel_id"
              value={formData.carrousel_id || ""}
              onChange={onChange}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="">Aucun carrousel</option>

              {galleries.map((gallery) => (
                <option key={gallery.id} value={gallery.id}>
                  {gallery.titre}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
            Les galeries proposées ici sont uniquement celles liées à cette
            association.
          </div>
        </div>
      </FormBlock>

      <div className="sticky bottom-0 z-10 flex justify-end border-t border-slate-200 bg-white/90 p-4 backdrop-blur dark:border-white/10 dark:bg-slate-900/90">
        <button
          type="submit"
          disabled={saving || demoMode}
          className="inline-flex items-center justify-center rounded-2xl bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Enregistrement..." : "Enregistrer les modifications"}
        </button>
      </div>
    </form>
  );
}

function FormBlock({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">{title}</h3>

          {subtitle && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}

function ImageUrlField({
  label,
  name,
  value,
  onChange,
  fallback,
  objectFit = "cover",
}) {
  const preview = resolveMediaUrl(value, fallback);

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
      <p className="mb-3 text-sm font-bold text-slate-950 dark:text-white">
        {label}
      </p>

      <div className="aspect-video overflow-hidden rounded-[20px] bg-slate-100 dark:bg-slate-900">
        <img
          src={preview}
          alt={label}
          className={[
            "h-full w-full",
            objectFit === "contain" ? "object-contain p-4" : "object-cover",
          ].join(" ")}
          onError={(event) => {
            event.currentTarget.src = fallback;
          }}
        />
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
          URL
        </label>

        <input
          type="text"
          name={name}
          value={value ?? ""}
          onChange={onChange}
          placeholder="https://... ou /uploads/..."
          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-900 dark:text-white"
        />
      </div>
    </div>
  );
}